import type {
  CheckoutPaymentGatewayApi,
  CheckoutStudentApi,
} from "../api/landingCheckout.types";

export interface RazorpayCheckoutSuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayInstance {
  open: () => void;
  on: (
    event: string,
    handler: (response: {
      error?: { description?: string };
    }) => void
  ) => void;
}

interface RazorpayConstructor {
  new (options: Record<string, unknown>): RazorpayInstance;
}

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Payment is only available in the browser."));
  }

  if (window.Razorpay) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${RAZORPAY_SCRIPT_URL}"]`
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Razorpay checkout.")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout."));
    document.body.appendChild(script);
  });
}

/**
 * Resolve amount in paise.
 * New API sends a number in subunits (e.g. 172500).
 * Legacy string amounts are always rupees (e.g. "1725" / "1725.00") → ×100.
 */
function resolveAmountPaise(amount: number | string): number {
  if (typeof amount === "number") {
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("Invalid payment amount.");
    }
    return Math.round(amount);
  }

  const trimmed = String(amount).trim();
  if (!trimmed) {
    throw new Error("Invalid payment amount.");
  }

  const rupees = parseFloat(trimmed);
  if (!Number.isFinite(rupees) || rupees <= 0) {
    throw new Error("Invalid payment amount.");
  }
  return Math.round(rupees * 100);
}

function resolveRazorpayKey(gateway: CheckoutPaymentGatewayApi): string {
  const key = gateway.key_id?.trim() || gateway.txn_token?.trim() || "";
  if (!key) {
    throw new Error("Payment gateway key is missing.");
  }
  return key;
}

function resolvePrefill(
  gateway: CheckoutPaymentGatewayApi,
  student: CheckoutStudentApi
): { name: string; email: string; contact: string } {
  return {
    name: gateway.prefill?.name?.trim() || student.name || "",
    email: gateway.prefill?.email?.trim() || student.email || "",
    contact: gateway.prefill?.contact?.trim() || student.phone_number || "",
  };
}

export interface LaunchPaymentGatewayParams {
  gateway: CheckoutPaymentGatewayApi;
  student: CheckoutStudentApi;
  /** Shown in Razorpay checkout UI */
  description?: string;
  onDismiss?: () => void;
}

/**
 * Opens Razorpay Checkout and resolves with payment ids for verify-payment.
 * Does not mark enrollment complete — caller must POST verify-payment.
 */
export async function launchCheckoutPaymentGateway({
  gateway,
  student,
  description,
  onDismiss,
}: LaunchPaymentGatewayParams): Promise<RazorpayCheckoutSuccess> {
  const provider = gateway.provider?.trim().toLowerCase() ?? "";

  if (provider === "razorpay") {
    if (!gateway.order_id?.trim()) {
      throw new Error("Payment order id is missing.");
    }

    const key = resolveRazorpayKey(gateway);
    await loadRazorpayScript();

    if (!window.Razorpay) {
      throw new Error("Razorpay checkout is unavailable.");
    }

    const amountPaise = resolveAmountPaise(gateway.amount);
    const currency = gateway.currency?.trim() || "INR";
    const prefill = resolvePrefill(gateway, student);

    return new Promise<RazorpayCheckoutSuccess>((resolve, reject) => {
      const options: Record<string, unknown> = {
        key,
        amount: amountPaise,
        currency,
        name: "Gurusish",
        description: description?.trim() || "Class enrollment",
        order_id: gateway.order_id,
        prefill,
        theme: {
          color: "#5C88C5",
        },
        handler: (response: RazorpayCheckoutSuccess) => {
          const paymentId = response?.razorpay_payment_id?.trim();
          const orderId = response?.razorpay_order_id?.trim();
          const signature = response?.razorpay_signature?.trim();
          if (!paymentId || !orderId || !signature) {
            reject(new Error("Payment response is incomplete."));
            return;
          }
          resolve({
            razorpay_payment_id: paymentId,
            razorpay_order_id: orderId,
            razorpay_signature: signature,
          });
        },
        modal: {
          ondismiss: () => {
            onDismiss?.();
            reject(new Error("Payment cancelled."));
          },
        },
      };

      // Do not set callback_url here — it conflicts with handler + verify-payment.
      const rzp = new window.Razorpay!(options);
      rzp.on("payment.failed", (response) => {
        reject(
          new Error(
            response.error?.description || "Payment failed. Please try again."
          )
        );
      });
      rzp.open();
    });
  }

  if (gateway.callback_url?.trim()) {
    window.location.assign(gateway.callback_url);
    // Redirect leaves this tab; never resolves for verify flow.
    return new Promise(() => undefined);
  }

  throw new Error(
    `Unsupported payment provider: ${gateway.provider || "unknown"}.`
  );
}
